import { ComponentProps, forwardRef, Ref, Suspense } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Badge } from "@/kit/ui/badge";
import AutoComplete_, {
  AutoCompleteT,
  useAutoCompleteInputValue,
} from "./AutoComplete";
import EntityAvatar from "./EntityAvatar";
import {
  LazyEntitiesAutoCompleteSelectionQuery,
  EntityKind,
} from "./__generated__/LazyEntitiesAutoCompleteSelectionQuery.graphql";
import { useDebounced } from "@/utils/hooks";
import { useAutoComplete } from "./AutoComplete/common";
import { FormattedMessage } from "react-intl";
import { AutoCompleteContext } from "./AutoComplete/props";
import { cn } from "@/utils/tailwind";

const Query = graphql`
  query LazyEntitiesAutoCompleteSelectionQuery(
    $search: String
    $kinds: [EntityKind!]
  ) {
    entities(first: 7, search: $search, kinds: $kinds) {
      nodes {
        id
        username
        displayName
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
    }
  }
`;

interface Entity {
  id: string;
  username: string;
  displayName: string;
}

const AutoComplete: AutoCompleteT<Entity> = AutoComplete_;

export interface LazyEntitiesAutoCompleteSelectionProps extends Omit<
  ComponentProps<"input">,
  "value" | "onChange"
> {
  kind?: EntityKind | undefined;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

export const LazyEntitiesAutoCompleteSelection = forwardRef(
  function LazyEntitiesAutoCompleteSelection(
    {
      kind,
      value,
      onChange,
      ...inputProps
    }: LazyEntitiesAutoCompleteSelectionProps,
    ref: Ref<HTMLInputElement>,
  ) {
    const onSelectItem = (
      item: Entity,
      context: AutoCompleteContext<Entity>,
    ) => {
      onChange(item.id);
      context.reset();
    };

    const onPopItem = () => {
      onChange(undefined);
    };

    return (
      <AutoComplete.Root onSelectItem={onSelectItem} onPopItem={onPopItem}>
        <AutoComplete.SingleInput
          ref={ref}
          {...inputProps}
          complete={value !== undefined}
        >
          {value !== undefined && (
            <Badge>
              <Suspense>
                <EntityAvatar.Lazy id={value} size="4" label="" />
              </Suspense>
            </Badge>
          )}
        </AutoComplete.SingleInput>

        <AutoComplete.Portal>
          <Suspense>
            <SelectionList kind={kind} />
          </Suspense>
        </AutoComplete.Portal>
      </AutoComplete.Root>
    );
  },
);

function SelectionList({ kind }: { kind: EntityKind | undefined }) {
  const { onSelectItem } = useAutoComplete<Entity>();
  const search_ = useAutoCompleteInputValue();
  const search = useDebounced(search_, 800);
  const {
    entities: { nodes },
  } = useLazyLoadQuery<LazyEntitiesAutoCompleteSelectionQuery>(Query, {
    search,
    kinds: kind ? [kind] : undefined,
  });
  if (nodes.length <= 0) {
    return (
      <p className="px-2 py-1 text-sm text-grey-600">
        <FormattedMessage defaultMessage="No match..." />
      </p>
    );
  }
  const bouncing = search_ !== search;
  return (
    <AutoComplete.List
      className={cn(bouncing && "opacity-80 pointer-events-none")}
    >
      {nodes.map((node) => (
        <AutoComplete.ListItem
          key={node.id}
          value={node}
          onClick={onSelectItem.bind(null, node)}
          className="cursor-pointer"
          role="button"
        >
          <span className="flex-1">{node.displayName}</span>
          <span className="text-grey-600">@{node.username}</span>
        </AutoComplete.ListItem>
      ))}
    </AutoComplete.List>
  );
}
